import { DatePicker } from '../../components/ui/date-picker';
import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody } from '../../components/ui/form-dialog';
// apps/web/src/pages/cadastros/celula-turno-calendario-form.tsx — Fase 03, Plano 02 (CAD-03, D-01).
import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import type { TurnoEntity, CalendarioExcecaoEntity } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';

const DIAS_SEMANA = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

interface CelulaTurnoCalendarioModalProps {
  celulaId: string;
  celulaNome: string;
  celulaCodigo: string;
  onClose: () => void;
}

function ehTurno(valor: unknown): valor is TurnoEntity {
  if (typeof valor !== 'object' || valor === null) return false;
  const turno = valor as Record<string, unknown>;
  return (
    typeof turno.id === 'string' && typeof turno.celulaId === 'string' &&
    typeof turno.diaSemana === 'number' && typeof turno.horaInicio === 'string' &&
    typeof turno.horaFim === 'string' && typeof turno.horasPadrao === 'number' &&
    typeof turno.createdAt === 'string'
  );
}

function ehExcecaoCalendario(valor: unknown): valor is CalendarioExcecaoEntity {
  if (typeof valor !== 'object' || valor === null) return false;
  const excecao = valor as Record<string, unknown>;
  return (
    typeof excecao.id === 'string' && typeof excecao.celulaId === 'string' &&
    typeof excecao.data === 'string' && typeof excecao.horasIndisponiveis === 'number' &&
    (typeof excecao.motivo === 'string' || excecao.motivo === null) && typeof excecao.createdAt === 'string'
  );
}

export const CelulaTurnoCalendarioModal: React.FC<CelulaTurnoCalendarioModalProps> = ({
  celulaId,
  celulaNome,
  celulaCodigo,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'turnos' | 'calendario'>('turnos');
  const [turnos, setTurnos] = useState<Record<number, { inicio: string; fim: string; horas: number }>>({});
  const [excecoes, setExcecoes] = useState<CalendarioExcecaoEntity[]>([]);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  // Nova exceção
  const [novaData, setNovaData] = useState(new Date().toISOString().slice(0, 10));
  const [novasHoras, setNovasHoras] = useState(8);
  const [novoMotivo, setNovoMotivo] = useState('');

  const carregarDados = async () => {
    try {
      const [resTurnos, resExcecoes] = await Promise.all([
        fetch(`/api/v1/planejamento/celulas/${celulaId}/turnos`),
        fetch(`/api/v1/planejamento/celulas/${celulaId}/excecoes?inicio=2026-01-01&fim=2026-12-31`),
      ]);

      if (resTurnos.ok) {
        const dadosTurnos: unknown = await resTurnos.json();
        if (!Array.isArray(dadosTurnos)) throw new Error('Turnos inválidos');
        const map: Record<number, { inicio: string; fim: string; horas: number }> = {};
        dadosTurnos.filter(ehTurno).forEach((t) => {
          map[t.diaSemana] = { inicio: t.horaInicio, fim: t.horaFim, horas: t.horasPadrao };
        });
        setTurnos(map);
      }

      if (resExcecoes.ok) {
        const dadosExcecoes: unknown = await resExcecoes.json();
        if (!Array.isArray(dadosExcecoes)) throw new Error('Exceções inválidas');
        setExcecoes(dadosExcecoes.filter(ehExcecaoCalendario));
      }
    } catch {
      setMsg({ tipo: 'erro', texto: 'Falha ao carregar turnos e calendário' });
    } finally {
    }
  };

  useEffect(() => {
    void carregarDados();
  }, [celulaId]);

  const handleSalvarTurno = async (dia: number) => {
    const config = turnos[dia];
    if (!config) return;

    try {
      const res = await apiMutate(`/api/v1/planejamento/celulas/${celulaId}/turnos`, {
        method: 'POST',
        body: {
          diaSemana: dia,
          horaInicio: config.inicio,
          horaFim: config.fim,
          horasPadrao: Number(config.horas),
        },
      });

      if (!res.ok) throw new Error('Erro ao salvar turno');
      setMsg({ tipo: 'ok', texto: `Turno de ${DIAS_SEMANA[dia]} salvo com sucesso!` });
    } catch {
      setMsg({ tipo: 'erro', texto: 'Falha ao salvar turno' });
    }
  };

  const handleAdicionarExcecao = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await apiMutate(`/api/v1/planejamento/celulas/${celulaId}/excecoes`, {
        method: 'POST',
        body: {
          data: novaData,
          horasIndisponiveis: Number(novasHoras),
          motivo: novoMotivo || undefined,
        },
      });

      if (!res.ok) throw new Error('Erro ao adicionar exceção');
      setNovoMotivo('');
      setMsg({ tipo: 'ok', texto: 'Exceção adicionada com sucesso!' });
      void carregarDados();
    } catch {
      setMsg({ tipo: 'erro', texto: 'Falha ao adicionar exceção de calendário' });
    }
  };

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        {/* Header */}
        <FormDialogHeader>
          <div>
            <FormDialogTitle>
              Turnos & Calendário — {celulaNome} ({celulaCodigo})
            </FormDialogTitle>
            <p className="text-xs text-slate-500">
              Configuração base para o cálculo finita-consciente de capacidade (D-01).
            </p>
          </div>

        </FormDialogHeader>

        {/* Tabs */}
        <div className="flex border-b bg-slate-50 px-5 gap-4">
          <button
            onClick={() => setActiveTab('turnos')}
            className={`py-3 text-xs font-semibold border-b-2 flex items-center gap-1.5 ${
              activeTab === 'turnos'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Clock className="w-4 h-4" />
            Turno Recorrente por Dia da Semana
          </button>
          <button
            onClick={() => setActiveTab('calendario')}
            className={`py-3 text-xs font-semibold border-b-2 flex items-center gap-1.5 ${
              activeTab === 'calendario'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Exceções de Calendário / Paradas
          </button>
        </div>

        {/* Feedback Alert */}
        {msg && (
          <div
            className={`mx-5 mt-4 p-2.5 rounded-md text-xs flex items-center gap-2 ${
              msg.tipo === 'ok'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-rose-50 text-rose-800 border border-rose-200'
            }`}
          >
            {msg.tipo === 'ok' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{msg.texto}</span>
          </div>
        )}

        {/* Content Body */}
        <FormDialogBody className="space-y-4">
          {activeTab === 'turnos' ? (
            <div className="space-y-2.5">
              {DIAS_SEMANA.map((nomeDia, dia) => {
                const config = turnos[dia] ?? { inicio: '08:00', fim: '17:00', horas: 8 };
                return (
                  <div
                    key={dia}
                    className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border bg-slate-50/50 hover:bg-slate-50"
                  >
                    <span className="text-xs font-semibold text-slate-800 w-32">{nomeDia}</span>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-500">Início:</span>
                        <input
                          type="time"
                          value={config.inicio}
                          onChange={(e) =>
                            setTurnos((prev) => ({
                              ...prev,
                              [dia]: { ...config, inicio: e.target.value },
                            }))
                          }
                          className="px-2 py-1 text-xs border rounded bg-white"
                        />
                      </div>

                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-500">Fim:</span>
                        <input
                          type="time"
                          value={config.fim}
                          onChange={(e) =>
                            setTurnos((prev) => ({
                              ...prev,
                              [dia]: { ...config, fim: e.target.value },
                            }))
                          }
                          className="px-2 py-1 text-xs border rounded bg-white"
                        />
                      </div>

                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-500">Horas:</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="24"
                          value={config.horas}
                          onChange={(e) =>
                            setTurnos((prev) => ({
                              ...prev,
                              [dia]: { ...config, horas: Number(e.target.value) },
                            }))
                          }
                          className="w-16 px-2 py-1 text-xs border rounded bg-white font-mono"
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => void handleSalvarTurno(dia)}
                      className="px-3 py-1 bg-slate-900 text-white rounded text-[11px] font-semibold hover:bg-slate-800"
                    >
                      Salvar
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Form de Nova Exceção */}
              <form
                onSubmit={(event) => void handleAdicionarExcecao(event)}
                className="p-3 border rounded-lg bg-slate-50 space-y-3"
              >
                <span className="text-xs font-semibold text-slate-800">
                  Adicionar Parada / Feriado / Manutenção
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-600 block" htmlFor="field-celula-turno-calendario-form-9888">Data</label>
                    <DatePicker id="field-celula-turno-calendario-form-9888"
                      value={novaData}
                      onValueChange={(value) => setNovaData(value)}
                      className="w-full px-2 py-1.5 text-xs border rounded bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 block" htmlFor="field-celula-turno-calendario-form-10300">Horas Indisponíveis</label>
                    <input id="field-celula-turno-calendario-form-10300"
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="24"
                      value={novasHoras}
                      onChange={(e) => setNovasHoras(Number(e.target.value))}
                      className="w-full px-2 py-1.5 text-xs border rounded bg-white font-mono"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 block" htmlFor="field-celula-turno-calendario-form-10876">Motivo</label>
                    <input id="field-celula-turno-calendario-form-10876"
                      type="text"
                      placeholder="Ex: Feriado, Troca de Banho"
                      value={novoMotivo}
                      onChange={(e) => setNovoMotivo(e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border rounded bg-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-semibold hover:bg-slate-800"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Adicionar Exceção
                  </button>
                </div>
              </form>

              {/* Lista de Exceções */}
              <div className="space-y-1.5">
                <span className="text-xs font-semibold text-slate-700">Exceções Cadastradas</span>
                {excecoes.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center border rounded-md">
                    Nenhuma exceção de calendário cadastrada para esta célula.
                  </p>
                ) : (
                  <div className="divide-y border rounded-md overflow-hidden bg-white">
                    {excecoes.map((exc) => (
                      <div
                        key={exc.id}
                        className="px-3 py-2 flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-medium text-slate-800">{exc.data}</span>
                          <span className="text-rose-600 font-semibold">
                            -{exc.horasIndisponiveis}h
                          </span>
                          <span className="text-slate-500">{exc.motivo ?? 'Sem motivo informado'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </FormDialogBody>
      </FormDialogContent>
    </FormDialog>
  );
};
